package com.mgacreative.hotelru.feature.admin.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotelru.shared.ui.designsystem.components.HFButton
import com.mgacreative.hotelru.shared.ui.designsystem.components.HFTextField
import com.mgacreative.hotelru.shared.ui.designsystem.components.LanguageSelectorRow
import com.mgacreative.hotelru.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HotelProfileScreen(onBack: () -> Unit = {}) {
    val viewModel = koinViewModel<AdminViewModel>()
    val profile by viewModel.profile.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.loadDashboardData()
    }

    var name by remember(profile) { mutableStateOf(profile?.name ?: "") }
    var companyTitle by remember(profile) { mutableStateOf(profile?.companyTitle ?: "") }
    var phone by remember(profile) { mutableStateOf(profile?.phone ?: "") }
    var email by remember(profile) { mutableStateOf(profile?.email ?: "") }
    var address by remember(profile) { mutableStateOf(profile?.address ?: "") }
    var taxOffice by remember(profile) { mutableStateOf(profile?.taxOffice ?: "") }
    var taxNumber by remember(profile) { mutableStateOf(profile?.taxNumber ?: "") }
    var authorizedPerson by remember(profile) { mutableStateOf(profile?.authorizedPerson ?: "") }
    var socialMedia by remember(profile) { mutableStateOf(profile?.social_media ?: "") }

    val scrollState = rememberScrollState()

    Scaffold(
        containerColor = Color(0xFFF8FAFC),
        topBar = {
            Surface(
                color = Color.White,
                shadowElevation = 1.dp
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        IconButton(onClick = onBack) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = l10n("Geri"), tint = Color(0xFF0F172A))
                        }
                        Spacer(Modifier.width(8.dp))
                        Column {
                            Text(
                                text = l10n("Otel Profili & Bilgileri"),
                                fontSize = 18.sp,
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFF0F172A)
                            )
                            Text(
                                text = l10n("Tesis ve fatura yapılandırma ayarları"),
                                fontSize = 12.sp,
                                color = Color(0xFF64748B)
                            )
                        }
                    }

                    LanguageSelectorRow(isDarkTheme = false)
                }
            }
        }
    ) { padding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .verticalScrollbar(scrollState)
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(scrollState)
                    .padding(20.dp),
                verticalArrangement = Arrangement.spacedBy(20.dp)
            ) {
                // Card 1: Temel Tesis Bilgileri
                ProfileCard(
                    title = l10n("Temel Tesis Bilgileri"),
                    icon = Icons.Outlined.Apartment,
                    iconColor = Color(0xFF2563EB)
                ) {
                    HFTextField(
                        value = name,
                        onValueChange = { name = it },
                        label = l10n("Otel Ticari Adı")
                    )
                    Spacer(Modifier.height(12.dp))
                    HFTextField(
                        value = companyTitle,
                        onValueChange = { companyTitle = it },
                        label = l10n("Şirket / Otel Resmi Ünvanı")
                    )
                    Spacer(Modifier.height(12.dp))
                    HFTextField(
                        value = authorizedPerson,
                        onValueChange = { authorizedPerson = it },
                        label = l10n("Otel Yetkilisi (Adı Soyadı / Unvanı)")
                    )
                }

                // Card 2: İletişim & Lokasyon Bilgileri
                ProfileCard(
                    title = l10n("İletişim & Konum Bilgileri"),
                    icon = Icons.Outlined.ContactPhone,
                    iconColor = Color(0xFF0D9488)
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        Box(modifier = Modifier.weight(1f)) {
                            HFTextField(
                                value = phone,
                                onValueChange = { phone = it },
                                label = l10n("Telefon Numarası")
                            )
                        }
                        Box(modifier = Modifier.weight(1f)) {
                            HFTextField(
                                value = email,
                                onValueChange = { email = it },
                                label = l10n("E-posta Adresi")
                            )
                        }
                    }
                    Spacer(Modifier.height(12.dp))
                    HFTextField(
                        value = address,
                        onValueChange = { address = it },
                        label = l10n("Açık Adres")
                    )
                    Spacer(Modifier.height(12.dp))
                    HFTextField(
                        value = socialMedia,
                        onValueChange = { socialMedia = it },
                        label = l10n("Sosyal Medya Linki (Örn: Instagram)")
                    )
                }

                // Card 3: Resmi & Vergi Bilgileri
                ProfileCard(
                    title = l10n("Resmi & Vergi Bilgileri"),
                    icon = Icons.Outlined.ReceiptLong,
                    iconColor = Color(0xFFD97706)
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        Box(modifier = Modifier.weight(1f)) {
                            HFTextField(
                                value = taxOffice,
                                onValueChange = { taxOffice = it },
                                label = l10n("Vergi Dairesi")
                            )
                        }
                        Box(modifier = Modifier.weight(1f)) {
                            HFTextField(
                                value = taxNumber,
                                onValueChange = { taxNumber = it },
                                label = l10n("Vergi Numarası")
                            )
                        }
                    }
                }

                // Save Action
                HFButton(
                    text = l10n("Değişiklikleri Kaydet"),
                    onClick = {
                        viewModel.updateProfile(
                            name = name,
                            phone = phone,
                            email = email,
                            address = address,
                            socialMedia = socialMedia,
                            companyTitle = companyTitle,
                            taxOffice = taxOffice,
                            taxNumber = taxNumber,
                            authorizedPerson = authorizedPerson
                        )
                        onBack()
                    }
                )

                Spacer(Modifier.height(24.dp))
            }
        }
    }
}

@Composable
private fun ProfileCard(
    title: String,
    icon: ImageVector,
    iconColor: Color,
    content: @Composable ColumnScope.() -> Unit
) {
    Card(
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
        border = BorderStroke(1.dp, Color(0xFFE2E8F0)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(20.dp)) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.padding(bottom = 16.dp)
            ) {
                Box(
                    modifier = Modifier
                        .size(38.dp)
                        .clip(RoundedCornerShape(10.dp))
                        .background(iconColor.copy(alpha = 0.12f)),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        imageVector = icon,
                        contentDescription = null,
                        tint = iconColor,
                        modifier = Modifier.size(20.dp)
                    )
                }
                Spacer(Modifier.width(12.dp))
                Text(
                    text = title,
                    fontSize = 15.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color(0xFF0F172A)
                )
            }

            content()
        }
    }
}
